import { Dropdown } from '@delmonicos/shared-design-system-react/src/components/dropdown/dropdown';
import { TicketStatus } from '@delmonicos/shared-sdk/libs/cpms/src/libs/core/src/enums/ticket-status.enum';
import { useEffect, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { useCpmsBoUpdateTicketMutation } from '@service/cpms-bo-ticket.service';

interface TicketStatusComponentProps {
  status: TicketStatus;
  id: string;
}

export const UpdateStatusComponent = ({
  status,
  id,
}: TicketStatusComponentProps): React.JSX.Element => {
  const { t } = useTranslation();
  const [updateTicket] = useCpmsBoUpdateTicketMutation();

  const statusForm = useForm<{ status: TicketStatus }>({
    defaultValues: {
      status,
    },
  });

  const newStatus = statusForm.watch('status');
  const isResetting = useRef(false);

  useEffect(() => {
    isResetting.current = true;
    statusForm.reset({ status });
  }, [status, id, statusForm]);

  useEffect(() => {
    if (isResetting.current) {
      isResetting.current = false;
      return;
    }
    if (isResetting.current || status === newStatus || !newStatus) return;

    updateTicket({ id, status: newStatus })
      .unwrap()
      .then(() => {
        toast.success(t('$.ticket-details.status-edit.success'));
      })
      .catch(() => {
        toast.error(t('$.ticket-details.status-edit.error'));
      });
  }, [t, updateTicket, id, status, newStatus]);

  return (
    <Controller
      name="status"
      control={statusForm.control}
      render={({ field }) => (
        <Dropdown
          {...field}
          value={field.value ?? ''}
          onChange={(value) => field.onChange(value ?? '')}>
          <Dropdown.Option hidden disabled key="" value="" />
          <Dropdown.Option value={TicketStatus.New}>
            {t('delmonicos-shared-sdk.enums-ticket-status.new')}
          </Dropdown.Option>
          <Dropdown.Option value={TicketStatus.InProgress}>
            {t('delmonicos-shared-sdk.enums-ticket-status.in-progress')}
          </Dropdown.Option>
          <Dropdown.Option value={TicketStatus.Closed}>
            {t('delmonicos-shared-sdk.enums-ticket-status.closed')}
          </Dropdown.Option>
        </Dropdown>
      )}
    />
  );
};
